import type { ProductFilterState } from '~/types'

function parseNumberList(raw: unknown): number[] | undefined {
  if (!raw) return undefined
  const values = Array.isArray(raw) ? raw : String(raw).split(',')
  const ids = values.map((value) => Number(value)).filter((id) => id > 0)
  return ids.length ? ids : undefined
}

function parseStringList(raw: unknown): string[] | undefined {
  if (!raw) return undefined
  const values = Array.isArray(raw) ? raw : String(raw).split(',')
  const items = values.map((value) => String(value).trim()).filter(Boolean)
  return items.length ? items : undefined
}

/** Storefront listing filters that each shop turns on from Setări → Magazin (all off by default). */
export const PRODUCT_FILTER_SETTING_KEYS = {
  onSale: 'product_filter_on_sale_enabled',
  brand: 'product_filter_brand_enabled',
  collapsible: 'product_filter_groups_collapsible',
} as const

/** Shows the „Recomandate” sort (pinned per category, then featured, then newest). Off by default. */
export const PRODUCT_SORT_RECOMMENDED_KEY = 'product_sort_recommended_enabled'

export const PRODUCT_SORT_OPTIONS = [
  { label: 'Recomandate', value: 'recommended' },
  { label: 'Cele mai noi', value: 'createdAt' },
  { label: 'Cel mai mare discount', value: 'discount' },
  { label: 'Preț crescător', value: 'price-asc' },
  { label: 'Preț descrescător', value: 'price-desc' },
  { label: 'Nume A-Z', value: 'name' },
  { label: 'Evaluare', value: 'rating' },
] as const

export type ProductSortUiValue = typeof PRODUCT_SORT_OPTIONS[number]['value']

export const PRODUCT_DEFAULT_SORT: ProductSortUiValue = 'createdAt'

export function visibleProductSortOptions(options: { reviewsEnabled: boolean, recommendedEnabled: boolean }) {
  return PRODUCT_SORT_OPTIONS.filter((option) =>
    (option.value !== 'rating' || options.reviewsEnabled)
    && (option.value !== 'recommended' || options.recommendedEnabled),
  )
}

export function parseProductDefaultSort(raw?: string | null): ProductSortUiValue {
  const value = String(raw || '').trim()
  if (PRODUCT_SORT_OPTIONS.some(option => option.value === value)) {
    return value as ProductSortUiValue
  }
  return PRODUCT_DEFAULT_SORT
}

export function sortStateFromUiValue(
  value?: string | null,
): Pick<ProductFilterState, 'sortBy' | 'sortOrder'> {
  const sortBy = parseProductDefaultSort(value)
  if (sortBy === 'price-asc' || sortBy === 'price-desc') {
    return { sortBy, sortOrder: undefined }
  }
  if (sortBy === 'name') {
    return { sortBy: 'name', sortOrder: 'asc' }
  }
  return { sortBy, sortOrder: 'desc' }
}

export function countActiveProductFilters(filters: ProductFilterState): number {
  let count = 0
  count += filters.brandIds?.length ?? 0
  count += filters.models?.length ?? 0
  count += filters.colors?.length ?? 0
  count += filters.storages?.length ?? 0
  count += filters.attributeValueIds?.length ?? 0
  if (filters.minPrice != null) count += 1
  if (filters.maxPrice != null) count += 1
  if (filters.inStock) count += 1
  if (filters.onSale) count += 1
  if (filters.refurbishedOnly) count += 1
  count += filters.availabilityStatusIds?.length ?? 0
  if (filters.search) count += 1
  return count
}

export function toUiSortValue(
  sortBy?: ProductFilterState['sortBy'],
  sortOrder?: ProductFilterState['sortOrder'],
): NonNullable<ProductFilterState['sortBy']> {
  if (sortBy === 'price') {
    return sortOrder === 'asc' ? 'price-asc' : 'price-desc'
  }
  if (
    sortBy === 'name'
    || sortBy === 'rating'
    || sortBy === 'discount'
    || sortBy === 'recommended'
    || sortBy === 'price-asc'
    || sortBy === 'price-desc'
  ) {
    return sortBy
  }
  return 'createdAt'
}

export function applyUiSortToFilters(filters: ProductFilterState): ProductFilterState {
  const sortBy = toUiSortValue(filters.sortBy, filters.sortOrder)
  return {
    ...filters,
    ...sortStateFromUiValue(sortBy),
  }
}

export function parseProductFiltersFromQuery(
  query: Record<string, unknown>,
  defaultSort: ProductSortUiValue = PRODUCT_DEFAULT_SORT,
): ProductFilterState {
  const fallback = sortStateFromUiValue(defaultSort)
  const hasExplicitSort = Boolean(query.sortBy)
  let sortBy = hasExplicitSort
    ? String(query.sortBy)
    : String(fallback.sortBy)
  let sortOrder: ProductFilterState['sortOrder'] | undefined = hasExplicitSort
    ? (query.sortOrder as ProductFilterState['sortOrder']) || undefined
    : fallback.sortOrder

  if (sortBy === 'price') {
    sortBy = (sortOrder === 'asc' ? 'price-asc' : 'price-desc')
    sortOrder = undefined
  }

  return {
    page: Number(query.page) || 1,
    brandIds: parseNumberList(query.brandIds)
      ?? (query.brandId ? [Number(query.brandId)] : undefined),
    categoryId: query.categoryId ? Number(query.categoryId) : undefined,
    models: parseStringList(query.models)
      ?? (query.model ? [String(query.model)] : undefined),
    minPrice: query.minPrice ? Number(query.minPrice) : undefined,
    maxPrice: query.maxPrice ? Number(query.maxPrice) : undefined,
    colors: parseStringList(query.colors)
      ?? (query.color ? [String(query.color)] : undefined),
    storages: parseStringList(query.storages)
      ?? (query.storage ? [String(query.storage)] : undefined),
    inStock: query.inStock === 'true',
    onSale: query.onSale === 'true' || undefined,
    refurbishedOnly: query.refurbishedOnly === 'true' || undefined,
    availabilityStatusIds: parseNumberList(query.availabilityStatusIds),
    search: query.search ? String(query.search) : undefined,
    attributeValueIds: parseNumberList(query.attributeValueIds),
    sortBy: (hasExplicitSort
      ? toUiSortValue(sortBy as ProductFilterState['sortBy'], sortOrder)
      : fallback.sortBy),
    sortOrder: hasExplicitSort
      ? (sortBy === 'price-asc' || sortBy === 'price-desc' ? undefined : sortOrder)
      : fallback.sortOrder,
  }
}

export function productFiltersToQuery(
  filters: ProductFilterState,
  defaultSort: ProductSortUiValue = PRODUCT_DEFAULT_SORT,
): Record<string, string> {
  const query: Record<string, string> = {}

  if (filters.page && filters.page > 1) query.page = String(filters.page)
  if (filters.brandIds?.length) query.brandIds = filters.brandIds.join(',')
  if (filters.categoryId) query.categoryId = String(filters.categoryId)
  if (filters.models?.length) query.models = filters.models.join(',')
  if (filters.minPrice != null) query.minPrice = String(filters.minPrice)
  if (filters.maxPrice != null) query.maxPrice = String(filters.maxPrice)
  if (filters.colors?.length) query.colors = filters.colors.join(',')
  if (filters.storages?.length) query.storages = filters.storages.join(',')
  if (filters.inStock) query.inStock = 'true'
  if (filters.onSale) query.onSale = 'true'
  if (filters.refurbishedOnly) query.refurbishedOnly = 'true'
  if (filters.availabilityStatusIds?.length) {
    query.availabilityStatusIds = filters.availabilityStatusIds.join(',')
  }
  if (filters.search) query.search = filters.search
  if (filters.attributeValueIds?.length) {
    query.attributeValueIds = filters.attributeValueIds.join(',')
  }

  const uiSort = toUiSortValue(filters.sortBy, filters.sortOrder)
  if (uiSort !== parseProductDefaultSort(defaultSort)) {
    if (uiSort === 'price-asc') {
      query.sortBy = 'price'
      query.sortOrder = 'asc'
    } else if (uiSort === 'price-desc') {
      query.sortBy = 'price'
      query.sortOrder = 'desc'
    } else {
      query.sortBy = uiSort
      const sortOrder = sortStateFromUiValue(uiSort).sortOrder
      if (sortOrder) query.sortOrder = sortOrder
    }
  }

  return query
}

export function normalizeProductFilters(filters: ProductFilterState): ProductFilterState {
  const normalized = { ...filters, page: filters.page ?? 1 }

  const sortBy = normalized.sortBy as string | undefined
  if (sortBy === 'price-asc') {
    normalized.sortBy = 'price'
    normalized.sortOrder = 'asc'
  } else if (sortBy === 'price-desc') {
    normalized.sortBy = 'price'
    normalized.sortOrder = 'desc'
  }

  return normalized
}
